import { useMutation, useQuery } from "@tanstack/react-query";

import {
  adminGrantBundleAccess,
  getUserBundleAccesses,
  updateUserBundleAccess,
} from "@/api";
import type { Status } from "@/types";
import { useDebouncedQuery } from "./useDebouncedQuery";

export const userBundleAccessQueryKeys = {
  useUserBundleAccess: () => ["userBundleAccess", "getUserBundleAccess"],
  useUserBundleAccessPage: (
    page: number,
    searchQuery: string,
    bundleIds: string[],
    statuses: string[],
  ) => [
    "userBundleAccess",
    "getUserBundleAccess",
    page,
    searchQuery,
    bundleIds,
    statuses,
  ],
  useUserBundleAccessForUser: (userId: string) => [
    "userBundleAccess",
    "forUser",
    userId,
  ],
};

export const useUserBundleAccess = () => {
  const useAllUserBundleAccess = (
    page: number,
    searchQuery: string,
    bundleIds: string[],
    statuses: Status[],
  ) =>
    useDebouncedQuery({
      queryFn: () =>
        getUserBundleAccesses(page, searchQuery, bundleIds, statuses),
      queryKey: userBundleAccessQueryKeys.useUserBundleAccessPage(
        page,
        searchQuery,
        bundleIds,
        statuses,
      ),
    });

  const useUpdateUserBundleAccess = () =>
    useMutation({ mutationFn: updateUserBundleAccess });

  const useAdminGrantBundleAccess = () =>
    useMutation({ mutationFn: adminGrantBundleAccess });

  const useUserBundleAccessesForUser = (userId: string) =>
    useQuery({
      queryFn: () => getUserBundleAccesses(1, "", [], [], userId, 100),
      queryKey: userBundleAccessQueryKeys.useUserBundleAccessForUser(userId),
      enabled: !!userId,
    });

  return {
    useAllUserBundleAccess,
    useUpdateUserBundleAccess,
    useAdminGrantBundleAccess,
    useUserBundleAccessesForUser,
  };
};
